import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import config from "../../config";
import { Row, Col, Button, Skeleton, DatePicker, Form, Result, Table } from "antd";
import { NavLink } from "react-router-dom";
import { PrinterOutlined, InboxOutlined } from "@ant-design/icons";
import moment from "moment";
import LoginChecked from "../Shared/LoginChecked";
import { NumberFormat } from "../Shared/Utility";
import { parseReportAmount } from "../../Pdf/PrintFormateNumber";

function formatAmount(value) {
  if (value === null || value === undefined || value === "") return "—";
  return NumberFormat(parseReportAmount(value));
}

function buildTradingRows(trading, grossProfit) {
  const isProfit = parseReportAmount(grossProfit) > 0;
  const isLoss = parseReportAmount(grossProfit) < 0;
  const lossAmount = isLoss ? Math.abs(parseReportAmount(grossProfit)) : 0;
  const profitAmount = isProfit ? parseReportAmount(grossProfit) : 0;

  const debitRows = [
    { side: "Dr", label: "Opening stock (at cost)", amount: trading.OpeningStock },
    { side: "Dr", label: "Purchases (period, at cost)", amount: trading.TotalPurchase },
  ];
  if (isProfit) {
    debitRows.push({ side: "Dr", label: "Gross profit", amount: profitAmount });
  }

  const creditRows = [
    { side: "Cr", label: "Sales (period, revenue)", amount: trading.TotalSales },
    { side: "Cr", label: "Closing stock (at cost)", amount: trading.ClosingStock },
  ];
  if (isLoss) {
    creditRows.push({ side: "Cr", label: "Gross loss", amount: lossAmount });
  }

  const rowCount = Math.max(debitRows.length, creditRows.length);
  const rows = [];
  for (let i = 0; i < rowCount; i++) {
    rows.push({
      key: i,
      debitLabel: debitRows[i]?.label || "",
      debitAmount: debitRows[i]?.amount,
      creditLabel: creditRows[i]?.label || "",
      creditAmount: creditRows[i]?.amount,
    });
  }
  return rows;
}

function TradingAccounts() {
  const { RangePicker } = DatePicker;

  const [loading, setLoading] = useState(false);
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const [StartDate, SetStartDate] = useState(null);
  const [EndDate, SetEndDate] = useState(null);
  const [trading, setTrading] = useState({});
  const [tradingData, setTradingData] = useState({});

  useEffect(() => {
    document.title = "Trading Account";
    if (!StartDate || !EndDate || !UserID || !AccessKey) {
      setTradingData({});
      setTrading({});
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Report/GetTradingAccountReport",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID: UserID,
        AccessKey: AccessKey,
        StartDate: StartDate,
        EndDate: EndDate,
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setTradingData(response.data);
          setTrading(response.data.Trading || {});
        } else {
          setTradingData({});
          setTrading({});
        }
        setLoading(false);
      })
      .catch(function () {
        setTradingData({});
        setTrading({});
        setLoading(false);
      });
  }, [StartDate, EndDate, UserID, AccessKey]);

  const handleDateChange = useCallback((_date, value) => {
    if (!value || !value[0] || !value[1]) {
      SetStartDate(null);
      SetEndDate(null);
      return;
    }
    SetStartDate(value[0]);
    SetEndDate(value[1]);
  }, []);

  const disableDate = (current) => current && current > moment().endOf("day");

  const printHref =
    StartDate && EndDate
      ? `/report-trading/print-list/?StartDate=${encodeURIComponent(
          StartDate
        )}&EndDate=${encodeURIComponent(EndDate)}`
      : "#";

  const tableRows = useMemo(
    () => buildTradingRows(trading, tradingData.GrossProfit),
    [trading, tradingData.GrossProfit]
  );

  const columns = [
    {
      title: "Debit",
      dataIndex: "debitLabel",
      key: "debitLabel",
      width: "28%",
    },
    {
      title: "Amount (Rs.)",
      dataIndex: "debitAmount",
      key: "debitAmount",
      align: "right",
      width: "22%",
      render: (val, record) =>
        record.debitLabel ? formatAmount(val) : "",
    },
    {
      title: "Credit",
      dataIndex: "creditLabel",
      key: "creditLabel",
      width: "28%",
    },
    {
      title: "Amount (Rs.)",
      dataIndex: "creditAmount",
      key: "creditAmount",
      align: "right",
      width: "22%",
      render: (val, record) =>
        record.creditLabel ? formatAmount(val) : "",
    },
  ];

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h3 className="page-title">Trading Account</h3>
            <div className="header-actions">
              <NavLink
                to={printHref}
                target="_blank"
                style={{
                  pointerEvents: StartDate && EndDate ? "auto" : "none",
                  opacity: StartDate && EndDate ? 1 : 0.5,
                }}
              >
                <Button
                  type="dashed"
                  size="large"
                  shape="round"
                  disabled={!StartDate || !EndDate}
                >
                  <PrinterOutlined />
                  Print Report
                </Button>
              </NavLink>
            </div>
          </div>

          <p className="text-muted" style={{ marginBottom: 16, maxWidth: 820 }}>
            Trading account for the selected period. Opening and closing stock
            are valued at cost (product balance × last purchase rate). Purchases
            use line amounts; sales use selling revenue.{" "}
            <strong>Cost of goods sold</strong> = Opening stock + Purchases −
            Closing stock; <strong>Gross profit</strong> = Sales − COGS. Tax and
            sale/purchase returns are not included.
          </p>

          <div className="filter-wrap">
            <Form>
              <Form.Item label="Period">
                <RangePicker
                  allowClear
                  onChange={handleDateChange}
                  disabledDate={disableDate}
                />
              </Form.Item>
            </Form>
          </div>

          {loading ? (
            <Row>
              <Col xs={24} md={20}>
                <Skeleton active />
                <Skeleton active />
              </Col>
            </Row>
          ) : StartDate && EndDate ? (
            <Row justify="center">
              <Col xs={24} lg={20}>
                {StartDate && EndDate ? (
                  <p style={{ marginBottom: 12 }}>
                    <strong>Period:</strong>{" "}
                    {moment(StartDate).format(config.date_format)} to{" "}
                    {moment(EndDate).format(config.date_format)}
                  </p>
                ) : null}

                <p style={{ marginBottom: 16 }}>
                  <strong>Cost of goods sold:</strong>{" "}
                  {formatAmount(trading.CostOfGoodsSold)}
                </p>

                <Table
                  size="small"
                  bordered
                  pagination={false}
                  columns={columns}
                  dataSource={tableRows}
                  footer={() => (
                    <Row>
                      <Col span={6}>
                        <strong>Total (Debit)</strong>
                      </Col>
                      <Col span={6} style={{ textAlign: "right" }}>
                        <strong>
                          {formatAmount(tradingData.TotalAmountLeft)}
                        </strong>
                      </Col>
                      <Col span={6}>
                        <strong>Total (Credit)</strong>
                      </Col>
                      <Col span={6} style={{ textAlign: "right" }}>
                        <strong>
                          {formatAmount(tradingData.TotalAmountRight)}
                        </strong>
                      </Col>
                    </Row>
                  )}
                />
              </Col>
            </Row>
          ) : (
            <Result
              icon={<InboxOutlined style={{ color: "gray" }} />}
              title={
                <span style={{ color: "gray", fontSize: "16px" }}>
                  Select a date range to load the trading account
                </span>
              }
            />
          )}
        </div>
      </div>
    </>
  );
}

export default TradingAccounts;
